import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  UserPlus, Users, Send, Activity, Flag, AlertTriangle, XCircle, Award, CalendarDays,
  ArrowRight, Sparkles, Lock, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { ReferralLinkCard } from "@/pages/referrals/InviteCandidate";

function ExtraKpis({ data, navigate }) {
  const items = [
    ["rp-kpi-invites-sent", "Invitations Sent", data.invitations?.sent, "/referrals/invite"],
    ["rp-kpi-invites-responded", "Invitation Responses", data.invitations?.responded, "/referrals/invite"],
    ["rp-kpi-activities", "Activities Completed", data.activities_completed, "/referrals/activity"],
    ["rp-kpi-open-tasks", "Open Tasks", data.tasks?.open, "/tasks"],
    ["rp-kpi-overdue-tasks", "Overdue Tasks", data.tasks?.overdue, "/tasks"],
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5" data-testid="rp-kpi-grid-2">
      {items.map(([id, l, v, to]) => (
        <button key={id} onClick={() => navigate(to)} data-testid={id} className="text-left rounded-xl border border-slate-200 bg-white p-3 shadow-sm hover:shadow-md transition-shadow">
          <p className="text-2xl font-display font-bold text-slate-900">{v ?? 0}</p><p className="text-xs text-slate-500">{l}</p>
        </button>))}
      {Object.keys(data.by_status || {}).length > 0 && (
        <div className="col-span-2 md:col-span-5 flex flex-wrap gap-2" data-testid="rp-by-status">
          <span className="text-xs text-slate-500 self-center">By stage:</span>
          {Object.entries(data.by_status).map(([s, n]) => <span key={s} className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-700">{s}: <strong>{n}</strong></span>)}
        </div>)}
    </div>
  );
}

const RANGES = [["this_month", "This Month"], ["this_quarter", "This Quarter"], ["ytd", "Year to Date"], ["all", "All Time"]];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

const KPIS = [
  ["referrals_submitted", "Referrals Submitted", Send, "navy", ""],
  ["active_referrals", "Active Referrals", Activity, "teal", "active"],
  ["new_this_period", "New This Period", UserPlus, "indigo", ""],
  ["milestones_reached", "Milestones Reached", Flag, "amber", ""],
  ["needs_attention", "Needs Your Attention", AlertTriangle, "rose", "attention"],
  ["closed_inactive", "Closed / Inactive", XCircle, "slate", "closed"],
];
const TONE = {
  navy: "text-[#006090] bg-[#006090]/10", teal: "text-[#0093D6] bg-[#0093D6]/10",
  amber: "text-amber-600 bg-amber-100", indigo: "text-indigo-600 bg-indigo-100",
  rose: "text-rose-600 bg-rose-100", slate: "text-slate-500 bg-slate-100",
};

function KpiCard({ k, label, Icon, tone, value, onClick }) {
  return (
    <button onClick={onClick} data-testid={`rp-kpi-${k}`}
      className="text-left rounded-xl border border-slate-200 bg-white p-4 shadow-sm hover:shadow-md hover:border-slate-300 transition-all group">
      <div className="flex items-center justify-between">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${TONE[tone]}`}><Icon className="w-4.5 h-4.5" /></div>
        {onClick && <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500" />}
      </div>
      <p className="mt-3 text-3xl font-display font-bold text-slate-900">{value ?? "—"}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </button>
  );
}

export default function ReferralDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [range, setRange] = useState(() => localStorage.getItem("rp_range") || "this_month");
  const [data, setData] = useState(null);
  const [today, setToday] = useState(null);
  const [err, setErr] = useState(false);
  const first = (user?.name || "").trim().split(" ")[0] || "there";

  const load = useCallback(() => {
    setErr(false);
    api.get("/referral-partner/dashboard", { params: { range } })
      .then((r) => setData(r.data)).catch((e) => { setErr(true); toast.error(apiError(e)); });
    const start = new Date().toISOString().slice(0, 10);
    const end = new Date(Date.now() + 14 * 864e5).toISOString().slice(0, 10);
    api.get("/calendar/my", { params: { start, end } }).then((r) => setToday(r.data)).catch(() => setToday({ items: [] }));
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const setRangeP = (v) => { setRange(v); localStorage.setItem("rp_range", v); };
  const goList = (filter) => navigate(filter ? `/referrals/list?filter=${filter}` : "/referrals/list");

  const kpis = data?.kpis || {};
  const upcoming = [...(data?.upcoming || []).map((u) => ({ title: `${u.type === "follow_up" ? "Follow up" : u.type === "interview" ? "Interview" : "Meeting"}: ${u.name}`, start: String(u.occurred_at || "").length === 10 ? `${u.occurred_at}T12:00` : u.occurred_at })),
    ...(today?.items || []).filter((i) => i.start && new Date(i.start) >= new Date())].sort((a, b) => String(a.start).localeCompare(String(b.start))).slice(0, 5);

  return (
    <div data-testid="referral-dashboard-page">
      <PageHeader title={`Welcome, ${first}`}
        description="Track your referrals, submit new leads, and see how your candidates are progressing." />

      {/* Quick actions */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <Button className="bg-brand hover:bg-brand-strong text-white" onClick={() => navigate("/referrals/add")} data-testid="rp-quick-add-lead">
          <UserPlus className="w-4 h-4 mr-1.5" />Add Lead
        </Button>
        <Button variant="outline" onClick={() => navigate("/referrals/invite")} data-testid="rp-quick-invite">
          <Send className="w-4 h-4 mr-1.5" />Invite a Candidate
        </Button>
        <Button variant="outline" onClick={() => goList()} data-testid="rp-quick-my-referrals">
          <Users className="w-4 h-4 mr-1.5" />My Referrals
        </Button>
        <div className="ml-auto">
          <Select value={range} onValueChange={setRangeP}>
            <SelectTrigger className="h-9 w-40" data-testid="rp-range"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{RANGES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="mb-5"><ReferralLinkCard /></div>

      {err && !data ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-center" data-testid="rp-dashboard-error">
          <p className="text-rose-700 font-medium mb-3">We couldn't load your dashboard.</p>
          <Button variant="outline" onClick={load} data-testid="rp-retry">Retry</Button>
        </div>
      ) : data && kpis.referrals_submitted === 0 ? (
        <EmptyState icon={Sparkles} title="Ready to make your first referral?"
          description="Submit someone you know who'd be a great fit. We'll match or create their record, keep it attributed to you, and update you as they progress — you don't manage the recruiting process."
          action={<Button className="bg-brand hover:bg-brand-strong text-white" onClick={() => navigate("/referrals/add")} data-testid="rp-empty-add-lead"><UserPlus className="w-4 h-4 mr-1.5" />Add Lead</Button>}
          testId="rp-empty" />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5" data-testid="rp-kpi-grid">
            {KPIS.map(([k, label, Icon, tone, filter]) => (
              <KpiCard key={k} k={k} label={label} Icon={Icon} tone={tone} value={kpis[k]}
                onClick={() => (k === "referrals_submitted" || k === "new_this_period") ? goList() : goList(filter)} />
            ))}
          </div>
          {data && <ExtraKpis data={data} navigate={navigate} />}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Needs attention */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="rp-needs-attention">
              <h3 className="font-display font-semibold text-slate-900 mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" />Needs Your Attention</h3>
              {(data?.needs_attention || []).length ? (
                <div className="space-y-2">
                  {data.needs_attention.map((a) => (
                    <button key={a.person_id} onClick={() => navigate(`/referrals/${a.person_id}`)} data-testid={`rp-attention-${a.person_id}`}
                      className="w-full text-left rounded-lg border border-slate-100 hover:border-slate-300 p-2.5 transition-colors">
                      <p className="text-sm font-medium text-slate-800">{a.name}</p>
                      <p className="text-xs text-amber-600">{a.reason} · {a.action}</p>
                    </button>
                  ))}
                </div>
              ) : <div className="flex items-center gap-2 text-sm text-slate-400 py-6 justify-center"><CheckCircle2 className="w-4 h-4 text-emerald-500" />You're all caught up.</div>}
            </div>

            {/* Recent activity */}
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="rp-activity">
              <h3 className="font-display font-semibold text-slate-900 mb-3 flex items-center gap-2"><Activity className="w-4 h-4 text-[#0093D6]" />Recent Activity</h3>
              {(data?.activity || []).length ? (
                <div className="space-y-2.5">
                  {data.activity.map((a, i) => (
                    <div key={i} className="flex items-start gap-2.5 text-sm" data-testid={`rp-activity-${i}`}>
                      <Flag className="w-3.5 h-3.5 text-[#0093D6] mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-slate-700 truncate"><span className="font-medium">{a.name}</span> — {a.label}</p>
                        <p className="text-[11px] text-slate-400">{fmtDate(a.occurred_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-slate-400 py-6 text-center">No activity yet.</p>}
            </div>

            {/* Upcoming + earnings */}
            <div className="space-y-4">
              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="rp-upcoming">
                <h3 className="font-display font-semibold text-slate-900 mb-3 flex items-center gap-2"><CalendarDays className="w-4 h-4 text-indigo-500" />Upcoming</h3>
                {upcoming.length ? (
                  <div className="space-y-2">
                    {upcoming.map((e, i) => (
                      <div key={i} className="text-sm" data-testid={`rp-upcoming-${i}`}>
                        <p className="text-slate-700 truncate">{e.title || e.summary || "Event"}</p>
                        <p className="text-[11px] text-slate-400">{fmtWhen(e.start)}</p>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-slate-400 py-4 text-center">Nothing scheduled.</p>}
                <button onClick={() => navigate("/calendar")} className="mt-2 text-xs text-brand font-medium" data-testid="rp-open-calendar">Open My Calendar →</button>
              </div>

              <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 text-center" data-testid="rp-earnings-coming-soon">
                <Lock className="w-5 h-5 text-slate-400 mx-auto mb-1.5" />
                <p className="text-sm font-medium text-slate-600">My Earnings — Coming Soon</p>
                <p className="text-xs text-slate-400 mt-0.5">Referral rewards will appear here once enabled.</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
